import { useEffect, useState, useMemo } from "react";
import { api, formatTRY, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, TrendingUp, TrendingDown, Edit3, Search } from "lucide-react";
import { toast } from "sonner";
import { useMonth } from "@/context/MonthContext";
import { useAuth } from "@/context/AuthContext";

const INCOME_CATS = ["Hakediş", "Diğer"];
const EXPENSE_CATS = ["Personel Maaşları", "Kira", "Vergi", "Diğer Gider"];

export default function Transactions() {
  const { month } = useMonth();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "partner" || user?.role === "accountant";

  const [companies, setCompanies] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [cFilter, setCFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [type, setType] = useState("income");
  const [form, setForm] = useState({
    company_id:"", category:"", amount:"", description:"", date: `${month}-01`
  });

  const load = async () => {
    const [c, t] = await Promise.all([
      api.get("/companies"),
      api.get("/transactions", { params: { month } }),
    ]);
    setCompanies(c.data); setItems(t.data);
  };
  useEffect(() => { load(); }, [month]);
  useEffect(() => { if (!editId) setForm((f) => ({ ...f, date: `${month}-01` })); }, [month, editId]);

  const resetForm = () => {
    setEditId(null);
    setType("income");
    setForm({ company_id:"", category:"", amount:"", description:"", date:`${month}-01` });
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setType(t.type);
    setForm({
      company_id: t.company_id,
      category: t.category,
      amount: String(t.amount),
      description: t.description || "",
      date: t.date,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, type, amount: Number(form.amount) };
      if (editId) {
        await api.put(`/transactions/${editId}`, payload);
        toast.success("İşlem güncellendi");
      } else {
        await api.post("/transactions", payload);
        toast.success("İşlem eklendi");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const remove = async (id) => {
    if (!window.confirm("İşlemi silmek istiyor musunuz?")) return;
    await api.delete(`/transactions/${id}`); toast.success("Silindi"); load();
  };

  const companyName = (id) => companies.find(c=>c.id===id)?.name || "—";
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return items.filter(t => {
      if (tab !== "all" && t.type !== tab) return false;
      if (cFilter !== "all" && t.company_id !== cFilter) return false;
      if (!q) return true;
      return (
        (t.category || "").toLocaleLowerCase("tr").includes(q) ||
        (t.description || "").toLocaleLowerCase("tr").includes(q) ||
        companyName(t.company_id).toLocaleLowerCase("tr").includes(q) ||
        String(t.amount).includes(q)
      );
    });
  }, [items, tab, cFilter, search, companies]);
  const totalIncome = items.filter(t => t.type === "income").reduce((s,t)=>s+Number(t.amount), 0);
  const totalExpense = items.filter(t => t.type === "expense").reduce((s,t)=>s+Number(t.amount), 0);
  const cats = type === "income" ? INCOME_CATS : EXPENSE_CATS;

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[.25em] text-slate-500">Gelir & Gider</div>
          <h1 className="text-3xl font-bold tracking-tight fk-heading">İşlem Kayıtları</h1>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-tx-btn" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-500">
                <Plus className="w-4 h-4 mr-1.5"/> Yeni İşlem
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#131b2e] border-white/10">
              <DialogHeader><DialogTitle>{editId ? "İşlemi Düzenle" : "Yeni Gelir/Gider Kaydı"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <Tabs value={type} onValueChange={(v)=>{setType(v); setForm({...form, category:""});}}>
                  <TabsList className="grid w-full grid-cols-2 bg-slate-900/60">
                    <TabsTrigger value="income" data-testid="tx-type-income">Gelir</TabsTrigger>
                    <TabsTrigger value="expense" data-testid="tx-type-expense">Gider</TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="space-y-2">
                  <Label>Firma</Label>
                  <Select value={form.company_id} onValueChange={(v)=>setForm({...form, company_id:v})}>
                    <SelectTrigger data-testid="tx-company" className="bg-slate-900/60 border-slate-700">
                      <SelectValue placeholder="Seçin"/>
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Kategori</Label>
                  <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                    <SelectTrigger data-testid="tx-category" className="bg-slate-900/60 border-slate-700">
                      <SelectValue placeholder="Seçin"/>
                    </SelectTrigger>
                    <SelectContent>
                      {cats.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tutar (₺)</Label>
                    <Input required type="number" step="0.01" data-testid="tx-amount" value={form.amount}
                           onChange={(e)=>setForm({...form, amount:e.target.value})}
                           className="bg-slate-900/60 border-slate-700"/>
                  </div>
                  <div className="space-y-2">
                    <Label>Tarih</Label>
                    <Input required type="date" data-testid="tx-date" value={form.date}
                           onChange={(e)=>setForm({...form, date:e.target.value})}
                           className="bg-slate-900/60 border-slate-700"/>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Input data-testid="tx-desc" value={form.description}
                         onChange={(e)=>setForm({...form, description:e.target.value})}
                         className="bg-slate-900/60 border-slate-700"/>
                </div>

                <DialogFooter>
                  <Button type="submit" data-testid="tx-submit"
                          className={type==="income" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"}>
                    {editId ? "Güncelle" : "Kaydet"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Ay Toplam Gelir</div>
          <div className="text-2xl font-semibold fk-mono mt-2 text-emerald-400">{formatTRY(totalIncome)}</div>
        </div>
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Ay Toplam Gider</div>
          <div className="text-2xl font-semibold fk-mono mt-2 text-rose-400">{formatTRY(totalExpense)}</div>
        </div>
        <div className="fk-card p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Net (Ortak Payı Hariç)</div>
          <div className="text-2xl font-semibold fk-mono mt-2 text-indigo-300">{formatTRY(totalIncome - totalExpense)}</div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-900/60">
            <TabsTrigger value="all" data-testid="tab-all">Tümü</TabsTrigger>
            <TabsTrigger value="income" data-testid="tab-income">Gelir</TabsTrigger>
            <TabsTrigger value="expense" data-testid="tab-expense">Gider</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={cFilter} onValueChange={setCFilter}>
          <SelectTrigger data-testid="tx-filter-company" className="w-[200px] bg-slate-900/60 border-slate-700">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Firmalar</SelectItem>
            {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <Input data-testid="tx-search" value={search} onChange={(e)=>setSearch(e.target.value)}
                 placeholder="Ara: kategori, açıklama, firma, tutar…"
                 className="h-9 pl-9 bg-slate-900/60 border-slate-700" />
        </div>
      </div>

      <div className="fk-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[.02] text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Tarih</th>
                <th className="text-left px-5 py-3 font-medium">Tip</th>
                <th className="text-left px-5 py-3 font-medium">Firma</th>
                <th className="text-left px-5 py-3 font-medium">Kategori</th>
                <th className="text-left px-5 py-3 font-medium">Açıklama</th>
                <th className="text-right px-5 py-3 font-medium">Tutar</th>
                {canEdit && <th className="w-20"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((t) => (
                <tr key={t.id} data-testid={`tx-row-${t.id}`} className="hover:bg-white/[.02]">
                  <td className="px-5 py-3 fk-mono text-slate-400">{t.date}</td>
                  <td className="px-5 py-3">
                    {t.type === "income"
                      ? <span className="fk-pill bg-emerald-500/10 text-emerald-300 border border-emerald-500/25"><TrendingUp className="w-3 h-3"/> Gelir</span>
                      : <span className="fk-pill bg-rose-500/10 text-rose-300 border border-rose-500/25"><TrendingDown className="w-3 h-3"/> Gider</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-300">{companyName(t.company_id)}</td>
                  <td className="px-5 py-3">
                    <span className="fk-pill bg-slate-500/10 text-slate-300 border border-slate-500/25">{t.category}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-400 max-w-xs truncate">{t.description || "—"}</td>
                  <td className={`px-5 py-3 text-right fk-mono font-medium ${t.type==="income" ? "text-emerald-400" : "text-rose-400"}`}>
                    {t.type === "income" ? "+" : "−"} {formatTRY(t.amount)}
                  </td>
                  {canEdit && (
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={()=>openEdit(t)}
                                data-testid={`edit-tx-${t.id}`}
                                className="text-slate-500 hover:text-indigo-300 hover:bg-indigo-500/10 h-8 w-8 p-0">
                          <Edit3 className="w-4 h-4"/>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={()=>remove(t.id)}
                                data-testid={`del-tx-${t.id}`}
                                className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0">
                          <Trash2 className="w-4 h-4"/>
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">Bu ay için işlem yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
